<template>
  <div
    ref="containerRef"
    class="p-4"
    :class="{ 'bg-static-info-max rounded-sm': shouldDisplayBestDeal }"
  >
    <div
      class="group-focus-within:outline-default-hi rounded-sm relative flex size-full flex-col border py-12 no-underline bg-static-default-low z-[1]"
      :class="{
        'bg-static-default-low-disabled border-action-default-low-disabled':
          disabled,
        'bg-surface-brand-hi border-action-default-low-pressed hover:bg-surface-brand-hi hover:border-action-default-low-pressed':
          selected,
        'bg-static-default-low border-action-default-low hover:bg-static-default-low-hover':
          !disabled && !selected,
      }"
    >
      <div
        v-if="loading"
        class="absolute left-0 top-1/2 -mt-12 flex w-full justify-center"
      >
        <span class="relative">
          <RevSpinner aria-hidden class="text-action-default-hi" />
        </span>
      </div>

      <div class="m-auto flex w-full flex-row items-start pl-8 pr-16">
        <div
          v-if="icon || $slots.icon"
          class="shrink-0"
          :class="{ invisible: loading }"
          data-test="icon"
        >
          <slot name="icon">
            <component :is="icon" v-bind="iconProps" />
          </slot>
        </div>
        <div class="ml-16 flex grow flex-col" :class="{ invisible: loading }">
          <div
            class="flex grow flex-nowrap items-start text-left"
            :class="{
              'flex-row': !isSmall,
              'flex-col': isSmall,
            }"
          >
            <span
              class="mr-8 min-w-[100px] grow [word-break:break-word]"
              :class="{
                [`text-action-default-hi body-1-bold`]: selected,
                [`text-action-default-hi-disabled body-1`]: disabled,
                [`text-action-default-hi body-1`]: !disabled && !selected,
              }"
            >
              {{ label }}
            </span>
            <span
              class="body-2 flex shrink-0 items-center gap-4"
              :class="{
                'body-2-bold': hasFlashDeal,
                'text-action-default-low':
                  !disabled && !isGoodDeal && !dealType,
                'text-action-default-low-disabled': disabled,
                'text-static-success-hi':
                  !disabled &&
                  ((isGoodDeal &&
                    shouldDisplayGoodDeal &&
                    !isPriceOpportunityActive) ||
                    hasFlashDeal),
                'pt-3': !isSmall && !isGoodDeal,
                invisible: loading,
              }"
            >
              <IconArrowDownRight
                v-if="
                  isGoodDeal &&
                  shouldDisplayGoodDeal &&
                  !isPriceOpportunityActive
                "
                :alternative-text="i18n(translations.goodDeal)"
              />
              <slot name="price-prefix" />
              {{ price }}
            </span>
          </div>
          <div
            v-if="subLabel && (!addon || !selected)"
            class="caption pt-4 text-left"
            :class="{
              'text-action-default-low': !disabled,
              'text-action-default-low-disabled': disabled,
            }"
          >
            {{ subLabel }}
          </div>
        </div>
      </div>

      <div v-if="addon" class="w-full">
        <component :is="addon" />
      </div>

      <RevTag
        v-if="tagLabel && tagVariant"
        class="absolute -top-8 right-16"
        :class="{ invisible: loading }"
        :label="tagLabel"
        :variant="tagVariant"
      />
    </div>
    <ClientOnly>
      <AccordionTransition :condition="shouldDisplayBestDeal && isInView">
        <div class="px-12 py-8 flex gap-16">
          <IconMoney class="shrink-0" />
          <div>
            <div class="body-2-bold">{{ bestDeal.title }}</div>
            <div class="body-2 text-static-default-low">
              {{ bestDeal.description }}
            </div>
          </div>
        </div>
      </AccordionTransition>
    </ClientOnly>
    <ClientOnly>
      <AccordionTransition
        :condition="isContextualCalloutVisible"
        div-wrapper-class="-mt-16"
      >
        <PickerContextualCallout
          v-if="contextualCallout"
          :contextual-callout="{
            icon: contextualCalloutIcon,
            text: contextualCallout.text,
            type: contextualCallout.type,
          }"
        />
      </AccordionTransition>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
/**
 * This component is used in place of the RevPicker provided by the design system. We used to override some of its styles to fit our needs,
 * but several issues prevented us to continue this way:
 * - The ability to position the tag differently (every picker)
 * - The ability to add a sub label (new battery, sim)
 * This will of course need to be discussed after the migration to FSM to see if those changes belongs to the existing component (as a variant?)
 * or should live in our scope.
 */
import {
  type Component,
  type VNode,
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { tw } from '@backmarket/utils/string/tw'
import { RevColorSwatch } from '@ds/components/ColorSwatch'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag, type Variants as TagVariants } from '@ds/components/Tag'
import { IconArrowDownRight } from '@ds/icons/IconArrowDownRight'
import { IconDiamond } from '@ds/icons/IconDiamond'
import { IconMoney } from '@ds/icons/IconMoney'
import { IconSimUnavailable } from '@ds/icons/IconSimUnavailable'
import { requestAnimationFrame } from '@ds/utils/requestAnimationFrame'

import FilledDiamond from '../FilledDiamond/FilledDiamond.vue'
import PickerRound from '../PickerRound/PickerRound.vue'
import translations from '../StepOption/StepOption.translations'

import type { Prefixes } from './LargePicker.types'
import AccordionTransition from './components/AccordionTransition/AccordionTransition.vue'
import PickerContextualCallout from './components/PickerContextualCallout/PickerContextualCallout.vue'

const props = withDefaults(
  defineProps<{
    addon?: VNode | Component
    disabled?: boolean
    color?: string | null
    index: number
    label: string
    loading?: boolean
    price?: string
    selected?: boolean
    prefix?: Prefixes
    subLabel?: string
    tag?: string | { label: string; variant?: TagVariants }
    isGoodDeal?: boolean
    isSmall?: boolean
    bestDeal?: { shouldDisplay: boolean; title: string; description: string }
    dealType?: 'flash_sales' | 'seller_flash_sales'
    contextualCallout?: {
      icon: string
      text: string
      type: 'warning'
    } | null
  }>(),
  {
    addon: undefined,
    disabled: false,
    color: '',
    isGoodDeal: false,
    isSmall: false,
    loading: false,
    selected: false,
    subLabel: '',
    price: '',
    prefix: undefined,
    tag: undefined,
    bestDeal: () => ({ shouldDisplay: false, title: '', description: '' }),
    dealType: undefined,
    contextualCallout: null,
  },
)

const i18n = useI18n()
const experiments = useExperiments()
const marketplace = useMarketplace()

const containerRef = ref<HTMLElement | null>(null)
const isInView = ref(false)
const isContextualCalloutVisible = ref(false)

let intersectionObserver: IntersectionObserver | null = null
let visibilityTimer: ReturnType<typeof setTimeout> | null = null
let contextualCalloutTimer: ReturnType<typeof setTimeout> | null = null

onMounted(() => {
  intersectionObserver = new IntersectionObserver(([entry]) => {
    if (visibilityTimer !== null) clearTimeout(visibilityTimer)
    if (entry.isIntersecting) {
      visibilityTimer = setTimeout(() => {
        isInView.value = true
      }, 300)
    } else {
      isInView.value = false
    }
  })
  if (containerRef.value) {
    intersectionObserver.observe(containerRef.value)
  }

  contextualCalloutTimer = setTimeout(() => {
    requestAnimationFrame(() => {
      isContextualCalloutVisible.value = !!props.contextualCallout
    })
  }, 300)
})

onUnmounted(() => {
  intersectionObserver?.disconnect()
  if (visibilityTimer !== null) clearTimeout(visibilityTimer)
  if (contextualCalloutTimer !== null) clearTimeout(contextualCalloutTimer)
})

watch(
  () => !!props.contextualCallout,
  (val) => {
    if (contextualCalloutTimer !== null) clearTimeout(contextualCalloutTimer)
    isContextualCalloutVisible.value = val
  },
)

const hasFlashDeal = computed(
  () =>
    props.dealType === 'flash_sales' || props.dealType === 'seller_flash_sales',
)

const shouldDisplayGoodDeal = computed(() => {
  return (
    experiments['experiment.ppUpgradeForLess'] !== 'withUpgrade' &&
    marketplace.market.countryCode !== MarketCountryCode.JP
  )
})

const shouldDisplayBestDeal = computed(() => {
  return props.bestDeal.shouldDisplay && !props.disabled
})

const isPriceOpportunityActive = computed(() => {
  return experiments['experiment.ppPriceOpportunity'] === 'withPriceOpportunity'
})

const tagLabel = computed(() => {
  if (!props.tag) {
    return ''
  }

  return typeof props.tag === 'string' ? props.tag : props.tag?.label
})

const tagVariant = computed(() => {
  return typeof props.tag === 'string'
    ? 'primary'
    : (props.tag?.variant ?? 'primary')
})

const icon = computed(() => {
  switch (props.prefix) {
    case 'AnimatedDiamondOutlined':
    case 'AnimatedDiamondFilled':
      return RevIllustration
    case 'IconDiamond':
      return IconDiamond
    case 'FilledDiamond':
      return FilledDiamond
    case 'Rakuten':
      return RevIllustration
    case 'circle':
      return PickerRound
    case 'color':
      if (props.color) {
        return RevColorSwatch
      }

      return undefined
    default:
      return undefined
  }
})

const contextualCalloutIcon = computed(() => {
  if (
    props.contextualCallout?.icon &&
    props.contextualCallout.icon === 'IconSimUnavailable'
  ) {
    return IconSimUnavailable
  }

  return null
})

const iconProps = computed(() => {
  switch (props.prefix) {
    case 'AnimatedDiamondOutlined':
      return {
        alt: '',
        src: '/img/product/funnel/outline-diamond.gif',
        width: 24,
        height: 24,
      }
    case 'AnimatedDiamondFilled':
      return {
        alt: '',
        src: '/img/product/funnel/filled-diamond.gif',
        width: 24,
        height: 24,
      }
    case 'IconDiamond':
      return {
        class: tw`text-static-default-hi`,
      }
    case 'circle':
      return {
        index: props.index,
        selected: props.selected,
        disabled: props.disabled,
      }
    case 'Rakuten':
      return {
        alt: '',
        src: '/img/product/jp/points-rakuten.svg',
        width: 24,
        height: 24,
      }
    case 'color':
      return {
        color: props.color,
        class: tw`mt-4`,
      }
    default:
      return {}
  }
})
</script>
